import React from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import { colors, hairline, radii, spacing } from '../../theme';
import Icon, { IconName } from '../ui/Icon';
import Text from '../ui/Text';

/**
 * One choosable payment instrument: a method, a saved card, a wallet.
 *
 * A radio rather than a chevron. Every row in these lists picks something for
 * the one button at the foot of the page - none of them navigate - and a
 * chevron on a row that does not go anywhere is the same lie a disabled
 * checkout button tells.
 *
 * The whole row is the target, not just the radio. A 20pt circle is under the
 * 44pt minimum on its own, and nobody aims for it when the label is right
 * there.
 */

type Props = {
  title: string;
  subtitle?: string;
  /** Leading glyph. Ignored when `leading` is given. */
  icon?: IconName;
  /** Replaces the glyph - a card network's lettermark. */
  leading?: React.ReactNode;
  /** Chip after the title, for "Default". */
  badge?: string;
  selected: boolean;
  onPress: () => void;
  /**
   * Unusable, with the reason in `subtitle`.
   *
   * Used for a wallet that cannot cover the total. The row stays on the list
   * rather than being hidden: someone who topped that wallet up last week
   * needs to be told it is short, not left wondering where it went.
   */
  disabled?: boolean;
  /** Extra content under the subtitle - the UPI app marks. */
  children?: React.ReactNode;
};

function MethodRow({
  title,
  subtitle,
  icon,
  leading,
  badge,
  selected,
  onPress,
  disabled = false,
  children,
}: Props) {
  return (
    <Pressable
      accessibilityRole="radio"
      accessibilityState={{ selected, disabled }}
      accessibilityLabel={subtitle ? `${title}. ${subtitle}` : title}
      disabled={disabled}
      onPress={onPress}
      style={({ pressed }) => [
        styles.row,
        selected ? styles.rowOn : null,
        disabled ? styles.rowOff : null,
        pressed && !disabled ? styles.pressed : null,
      ]}
    >
      <View style={styles.lead}>
        {leading ?? (
          <Icon
            name={icon ?? 'creditCard'}
            size={20}
            color={
              disabled
                ? colors.inkDisabled
                : selected
                ? colors.primary
                : colors.inkMuted
            }
          />
        )}
      </View>

      <View style={styles.body}>
        <View style={styles.titleRow}>
          {/* The ink says unavailable now that `rowOff` no longer dims the
              whole row with an opacity that took this under 2:1. */}
          <Text
            variant="labelSm"
            numberOfLines={1}
            color={disabled ? colors.inkDisabled : undefined}
          >
            {title}
          </Text>
          {badge ? (
            <View style={styles.badge}>
              <Text variant="eyebrow" color={colors.primary}>
                {badge}
              </Text>
            </View>
          ) : null}
        </View>

        {subtitle ? (
          <Text
            variant="caption"
            numberOfLines={1}
            color={disabled ? colors.inkDisabled : undefined}
          >
            {subtitle}
          </Text>
        ) : null}

        {children}
      </View>

      {/*
        Filled-with-a-tick rather than a dot in a ring.

        At 20pt a dot inside a ring is two thin circles, and on the pale canvas
        the pair read as one soft blob - you cannot tell selected from not
        without looking twice. A solid disc is unmistakable across the width of
        a list.
      */}
      <View style={[styles.radio, selected ? styles.radioOn : null]}>
        {selected ? <Icon name="check" size={12} color={colors.onPrimary} strokeWidth={2.6} /> : null}
      </View>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.md,
    borderRadius: radii.lg,
    backgroundColor: colors.surface,
    ...hairline,
  },
  /* The outline carries the selection, not a fill: a tinted row beside three
     white ones reads as "unavailable" as often as it reads as "chosen". */
  rowOn: {
    borderColor: colors.primary,
  },
  /* Fill and ink rather than `opacity: 0.5` over the row - see the same
     change on `Button.inertFills`. */
  rowOff: {
    backgroundColor: colors.background,
  },
  pressed: {
    backgroundColor: colors.surfaceMuted,
  },
  lead: {
    width: 26,
    alignItems: 'center',
  },
  body: {
    flex: 1,
    gap: 2,
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  badge: {
    paddingHorizontal: spacing.sm,
    paddingVertical: 1,
    borderRadius: radii.sm,
    backgroundColor: colors.surfaceAccent,
  },
  radio: {
    width: 20,
    height: 20,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1.4,
    borderColor: colors.borderStrong,
  },
  radioOn: {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
  },
});

export default MethodRow;
